/**
 * AnamnesePublica — Ficha de anamnese online para o paciente preencher antes da consulta.
 * Rota pública, sem autenticação. Acesso via ?token=XXXX
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Ico, emo } from '../ui/Ico';

const API = process.env.REACT_APP_API_URL || 'https://levnutri.com/nutri/api';
const LOGO_URL = 'https://www.levnutri.com/assets/logo.png';
const BRAND = '#D4A574';
const BRAND2 = '#b8895c';

const DRAFT_KEY = (tok) => `lev_anamnese_draft_${tok}`;
// Versão da Política de Privacidade aceita (public/privacidade/index.html)
const CONSENT_VERSION = '1.0';

// ─── Helpers de estilo ────────────────────────────────────────────────────────

const S = {
  wrap: {
    minHeight: '100vh',
    background: 'linear-gradient(160deg, #fbf8f5 0%, #fbf8f5 100%)',
    fontFamily: 'Nunito, system-ui, sans-serif',
    padding: '0 0 4rem',
  },
  header: {
    background: '#ffffff',
    borderBottom: '1px solid rgba(212,165,116,0.2)',
    padding: '1rem 1.25rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  container: { maxWidth: '600px', margin: '0 auto', padding: '0 1rem' },
  progress: { background: '#f6f0ea', borderRadius: '999px', height: '6px', margin: '1rem 0 0.25rem', overflow: 'hidden' },
  card: {
    background: '#ffffff',
    borderRadius: '16px',
    padding: '1.5rem',
    boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
    border: '1px solid rgba(212,165,116,0.15)',
    marginTop: '1rem',
  },
  sectionTitle: {
    margin: '0 0 1.25rem',
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#2f2a26',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  label: {
    display: 'block',
    fontSize: '0.82rem',
    fontWeight: 600,
    color: '#5a3e2b',
    marginBottom: '0.35rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  input: {
    width: '100%',
    padding: '0.7rem 0.9rem',
    border: '1.5px solid #d9d0c6',
    borderRadius: '10px',
    fontSize: '0.95rem',
    color: '#2f2a26',
    background: '#fbf8f5',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  textarea: {
    width: '100%',
    padding: '0.7rem 0.9rem',
    border: '1.5px solid #d9d0c6',
    borderRadius: '10px',
    fontSize: '0.95rem',
    color: '#2f2a26',
    background: '#fbf8f5',
    outline: 'none',
    boxSizing: 'border-box',
    resize: 'vertical',
    minHeight: '80px',
    fontFamily: 'inherit',
  },
  select: {
    width: '100%',
    padding: '0.7rem 0.9rem',
    border: '1.5px solid #d9d0c6',
    borderRadius: '10px',
    fontSize: '0.95rem',
    color: '#2f2a26',
    background: '#fbf8f5',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    appearance: 'none',
    cursor: 'pointer',
  },
  field: { marginBottom: '1.1rem' },
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' },
  btnNext: {
    width: '100%',
    padding: '0.9rem',
    background: `linear-gradient(135deg, ${BRAND}, #e8c9a8)`,
    color: '#ffffff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '1rem',
    fontFamily: 'inherit',
  },
  btnPrev: {
    width: '100%',
    padding: '0.85rem',
    background: '#f6f0ea',
    color: '#5a3e2b',
    border: 'none',
    borderRadius: '12px',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '0.5rem',
    fontFamily: 'inherit',
  },
  optionRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
    gap: '0.5rem',
    marginTop: '0.35rem',
  },
  checkboxCard: (checked) => ({
    padding: '0.55rem 0.7rem',
    borderRadius: '8px',
    border: `1.5px solid ${checked ? BRAND : '#d9d0c6'}`,
    background: checked ? `${BRAND}18` : '#fbf8f5',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: checked ? 700 : 500,
    color: checked ? BRAND2 : '#4a4440',
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    userSelect: 'none',
    transition: 'all 0.15s',
  }),
  sliderWrap: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
};

// ─── Componentes genéricos ────────────────────────────────────────────────────

function Field({ label, required, hint, children }) {
  return (
    <div style={S.field}>
      <label style={S.label}>{emo(label)}{required && <span style={{ color: '#c46a60' }}> *</span>}</label>
      {emo(hint && <p style={{ margin: '-0.2rem 0 0.35rem', fontSize: '0.77rem', color: '#736b64' }}>{emo(hint)}</p>)}
      {children}
    </div>
  );
}

function SelectOpts({ name, value, onChange, options, placeholder }) {
  return (
    <div style={{ position: 'relative' }}>
      <select style={S.select} name={name} value={value} onChange={onChange}>
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      <span style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: '0.75rem', color: '#736b64' }}>▾</span>
    </div>
  );
}

function RadioCards({ name, value, onChange, options }) {
  return (
    <div style={S.optionRow}>
      {options.map(o => (
        <div key={o} style={S.checkboxCard(value === o)} onClick={() => onChange({ target: { name, value: o } })}>
          {value === o ? '●' : '○'} {o}
        </div>
      ))}
    </div>
  );
}

function CheckboxCards({ name, values, onChange, options }) {
  const toggle = (opt) => {
    const next = values.includes(opt) ? values.filter(v => v !== opt) : [...values, opt];
    onChange(name, next);
  };
  return (
    <div style={S.optionRow}>
      {options.map(o => (
        <div key={o} style={S.checkboxCard(values.includes(o))} onClick={() => toggle(o)}>
          {emo(values.includes(o) ? '☑' : '☐')} {o}
        </div>
      ))}
    </div>
  );
}

function SliderField({ label, name, value, min, max, step = 1, unit, onChange }) {
  return (
    <Field label={label}>
      <div style={S.sliderWrap}>
        <input
          type="range" min={min} max={max} step={step}
          value={value || min}
          onChange={onChange}
          name={name}
          style={{ flex: 1, accentColor: BRAND }}
        />
        <span style={{ minWidth: '3.5rem', textAlign: 'right', fontWeight: 600, color: BRAND2, fontSize: '1rem' }}>
          {value || min}{unit}
        </span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#a39b93', marginTop: '0.15rem' }}>
        <span>{min}{unit}</span><span>{max}{unit}</span>
      </div>
    </Field>
  );
}

// ─── SEÇÕES ───────────────────────────────────────────────────────────────────

function Secao1({ data, onChange, onChangeMulti }) {
  return (
    <>
      <h2 style={S.sectionTitle}><Ico e="👤" sp />Identificação</h2>
      <Field label="Nome completo" required>
        <input style={S.input} name="nome_completo" value={data.nome_completo || ''} onChange={onChange} placeholder="Seu nome completo" />
      </Field>
      <div style={S.row2}>
        <Field label="Data de nascimento" required>
          <input style={S.input} type="date" name="data_nascimento" value={data.data_nascimento || ''} onChange={onChange} />
        </Field>
        <Field label="Sexo" required>
          <SelectOpts name="sexo" value={data.sexo || ''} onChange={onChange} options={['Feminino', 'Masculino']} placeholder="Selecione" />
        </Field>
      </div>
      <Field label="WhatsApp" required>
        <input style={S.input} name="whatsapp" value={data.whatsapp || ''} onChange={onChange} placeholder="(00) 00000-0000" type="tel" />
      </Field>
      <Field label="E-mail">
        <input style={S.input} name="email" value={data.email || ''} onChange={onChange} placeholder="seu@email.com" type="email" />
      </Field>
      <div style={S.row2}>
        <Field label="Cidade">
          <input style={S.input} name="cidade" value={data.cidade || ''} onChange={onChange} placeholder="Ex: São Paulo" />
        </Field>
        <Field label="Estado">
          <SelectOpts name="estado" value={data.estado || ''} onChange={onChange}
            options={['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO']}
            placeholder="UF" />
        </Field>
      </div>
      <Field label="Profissão">
        <input style={S.input} name="profissao" value={data.profissao || ''} onChange={onChange} placeholder="Ex: Professora, Enfermeira..." />
      </Field>
      <Field label="Como nos conheceu?">
        <RadioCards name="como_conheceu" value={data.como_conheceu || ''}
          onChange={onChange}
          options={['Instagram', 'Indicação', 'Google', 'Facebook', 'TikTok', 'Outro']} />
      </Field>
    </>
  );
}

function Secao2({ data, onChange }) {
  return (
    <>
      <h2 style={S.sectionTitle}><Ico e="🎯" sp />Objetivos</h2>
      <Field label="O que te trouxe até aqui?" required hint="Conte-nos brevemente sobre sua queixa principal">
        <textarea style={S.textarea} name="queixa_principal" value={data.queixa_principal || ''} onChange={onChange}
          placeholder="Ex: Tenho ganhado peso depois da pandemia e me sinto sem energia..." rows={4} />
      </Field>
      <Field label="Objetivo principal" required>
        <SelectOpts name="objetivo_principal" value={data.objetivo_principal || ''} onChange={onChange}
          options={['Emagrecer', 'Ganhar massa', 'Equilíbrio hormonal', 'Mais energia', 'Reeducação alimentar', 'Manutenção do peso', 'Outro']}
          placeholder="Selecione seu objetivo" />
      </Field>
      <div style={S.row2}>
        <Field label="Peso atual (kg)">
          <input style={S.input} type="number" step="0.1" name="peso_atual" value={data.peso_atual || ''} onChange={onChange} placeholder="Ex: 72.5" />
        </Field>
        <Field label="Altura (cm)">
          <input style={S.input} type="number" name="altura" value={data.altura || ''} onChange={onChange} placeholder="Ex: 165" />
        </Field>
      </div>
      <Field label="Peso desejado (kg)">
        <input style={S.input} type="number" step="0.1" name="peso_desejado" value={data.peso_desejado || ''} onChange={onChange} placeholder="Ex: 62" />
      </Field>
      <Field label="Já fez acompanhamento nutricional antes?">
        <RadioCards name="tratamento_anterior" value={data.tratamento_anterior || ''} onChange={onChange} options={['Sim', 'Não']} />
      </Field>
      {data.tratamento_anterior === 'Sim' && (
        <Field label="Como foi? O que funcionou ou não?">
          <textarea style={S.textarea} name="tratamento_anterior_desc" value={data.tratamento_anterior_desc || ''} onChange={onChange}
            placeholder="Conte um pouco sobre sua experiência anterior..." rows={3} />
        </Field>
      )}
    </>
  );
}

function Secao3({ data, onChange, onChangeMulti }) {
  const doencas = data.doencas || [];
  return (
    <>
      <h2 style={S.sectionTitle}><Ico e="🏥" sp />Histórico de Saúde</h2>
      <Field label="Possui alguma doença diagnosticada?" hint="Selecione todas que se aplicam">
        <CheckboxCards name="doencas" values={doencas} onChange={onChangeMulti}
          options={['Diabetes', 'Hipertensão', 'Hipotireoidismo', 'Hipertireoidismo', 'SOP', 'Enxaqueca', 'Ansiedade', 'Depressão', 'Nenhuma']} />
      </Field>
      {doencas.length > 0 && !doencas.includes('Nenhuma') && (
        <Field label="Outra doença não listada?">
          <input style={S.input} name="doenca_outra" value={data.doenca_outra || ''} onChange={onChange} placeholder="Digite aqui..." />
        </Field>
      )}
      <Field label="Já realizou cirurgias?">
        <RadioCards name="cirurgias" value={data.cirurgias || ''} onChange={onChange} options={['Sim', 'Não']} />
      </Field>
      {data.cirurgias === 'Sim' && (
        <Field label="Quais cirurgias?">
          <input style={S.input} name="cirurgias_desc" value={data.cirurgias_desc || ''} onChange={onChange} placeholder="Ex: bariátrica, cesariana..." />
        </Field>
      )}
      <Field label="Usa algum medicamento?">
        <RadioCards name="medicamentos" value={data.medicamentos || ''} onChange={onChange} options={['Sim', 'Não']} />
      </Field>
      {data.medicamentos === 'Sim' && (
        <Field label="Quais medicamentos e doses?">
          <textarea style={S.textarea} name="medicamentos_desc" value={data.medicamentos_desc || ''} onChange={onChange}
            placeholder="Ex: Levotiroxina 50mcg, Metformina 500mg..." rows={3} />
        </Field>
      )}
      <Field label="Possui alergias alimentares?">
        <RadioCards name="alergias_alimentares" value={data.alergias_alimentares || ''} onChange={onChange} options={['Sim', 'Não']} />
      </Field>
      {data.alergias_alimentares === 'Sim' && (
        <Field label="Quais alergias?">
          <input style={S.input} name="alergias_alimentares_desc" value={data.alergias_alimentares_desc || ''} onChange={onChange} placeholder="Ex: amendoim, frutos do mar..." />
        </Field>
      )}
      <Field label="Possui intolerâncias alimentares?">
        <RadioCards name="intolerancia_alimentar" value={data.intolerancia_alimentar || ''} onChange={onChange} options={['Sim', 'Não']} />
      </Field>
      {data.intolerancia_alimentar === 'Sim' && (
        <Field label="Quais intolerâncias?">
          <input style={S.input} name="intolerancia_alimentar_desc" value={data.intolerancia_alimentar_desc || ''} onChange={onChange} placeholder="Ex: lactose, glúten..." />
        </Field>
      )}
      <Field label="Histórico familiar de doenças" hint="Ex: diabetes, hipertensão, câncer na família">
        <textarea style={S.textarea} name="historico_familiar" value={data.historico_familiar || ''} onChange={onChange}
          placeholder="Descreva histórico de doenças na família..." rows={3} />
      </Field>
    </>
  );
}

function Secao4({ data, onChange, onChangeMulti }) {
  return (
    <>
      <h2 style={S.sectionTitle}><Ico e="🍽" sp />Hábitos Alimentares</h2>
      <Field label="Quantas refeições faz por dia?">
        <RadioCards name="num_refeicoes" value={String(data.num_refeicoes || '')} onChange={onChange}
          options={['1', '2', '3', '4', '5', '6+']} />
      </Field>
      <Field label="Alimentos que mais gosta">
        <textarea style={S.textarea} name="alimentos_gosta" value={data.alimentos_gosta || ''} onChange={onChange}
          placeholder="Ex: frutas, arroz, frango, massas..." rows={2} />
      </Field>
      <Field label="Alimentos que não gosta ou evita">
        <textarea style={S.textarea} name="alimentos_nao_gosta" value={data.alimentos_nao_gosta || ''} onChange={onChange}
          placeholder="Ex: fígado, cebola, peixe..." rows={2} />
      </Field>
      <Field label="Restrições alimentares" hint="Selecione todas que se aplicam">
        <CheckboxCards name="restricoes_alimentares_multi" values={data.restricoes_alimentares_multi || []}
          onChange={onChangeMulti}
          options={['Vegetariana', 'Vegana', 'Sem glúten', 'Sem lactose', 'Sem carne vermelha', 'Nenhuma']} />
      </Field>
      <Field label="Consome bebida alcoólica?">
        <RadioCards name="consumo_alcool" value={data.consumo_alcool || ''} onChange={onChange}
          options={['Nunca', 'Raramente', 'Fins de semana', 'Frequentemente']} />
      </Field>
      <SliderField label="Consumo de água (litros/dia)" name="consumo_agua" value={data.consumo_agua} min={0.5} max={4} step={0.5} unit="L" onChange={onChange} />
      <Field label="Costuma comer fora de casa?">
        <RadioCards name="come_fora" value={data.come_fora || ''} onChange={onChange}
          options={['Nunca', 'Raramente', 'Às vezes', 'Frequentemente']} />
      </Field>
      <Field label="Tem compulsão alimentar?">
        <RadioCards name="compulsao_alimentar" value={data.compulsao_alimentar || ''} onChange={onChange}
          options={['Nunca', 'Às vezes', 'Frequentemente', 'Sempre']} />
      </Field>
      <Field label="Come por ansiedade ou estresse?">
        <RadioCards name="come_por_ansiedade" value={data.come_por_ansiedade || ''} onChange={onChange}
          options={['Nunca', 'Às vezes', 'Frequentemente', 'Sempre']} />
      </Field>
      <SliderField label="Preferência por doces (1 = nenhuma, 5 = muito forte)" name="pref_doces" value={data.pref_doces} min={1} max={5} unit="" onChange={onChange} />
      <SliderField label="Preferência por salgados (1 = nenhuma, 5 = muito forte)" name="pref_salgados" value={data.pref_salgados} min={1} max={5} unit="" onChange={onChange} />
    </>
  );
}

function Secao5({ data, onChange }) {
  return (
    <>
      <h2 style={S.sectionTitle}><Ico e="🏃" sp />Estilo de Vida</h2>
      <Field label="Pratica atividade física?">
        <RadioCards name="pratica_atividade" value={data.pratica_atividade || ''} onChange={onChange} options={['Sim', 'Não']} />
      </Field>
      {data.pratica_atividade === 'Sim' && (
        <>
          <Field label="Qual atividade?">
            <input style={S.input} name="tipo_atividade" value={data.tipo_atividade || ''} onChange={onChange} placeholder="Ex: musculação, caminhada, yoga..." />
          </Field>
          <div style={S.row2}>
            <Field label="Frequência (dias/semana)">
              <SelectOpts name="frequencia_atividade" value={data.frequencia_atividade || ''} onChange={onChange}
                options={['1', '2', '3', '4', '5', '6', '7']} placeholder="Dias/sem" />
            </Field>
            <Field label="Duração (minutos)">
              <input style={S.input} type="number" name="duracao_atividade" value={data.duracao_atividade || ''} onChange={onChange} placeholder="Ex: 60" />
            </Field>
          </div>
        </>
      )}
      <Field label="Qualidade do sono">
        <RadioCards name="qualidade_sono" value={data.qualidade_sono || ''} onChange={onChange}
          options={['Ótima', 'Boa', 'Regular', 'Ruim', 'Péssima']} />
      </Field>
      <SliderField label="Horas de sono por noite" name="horas_sono" value={data.horas_sono} min={4} max={12} unit="h" onChange={onChange} />
      <SliderField label="Nível de estresse no dia a dia (1 = baixo, 10 = muito alto)" name="nivel_estresse" value={data.nivel_estresse} min={1} max={10} unit="" onChange={onChange} />
      <Field label="Trabalha em que período?">
        <RadioCards name="periodo_trabalho" value={data.periodo_trabalho || ''} onChange={onChange}
          options={['Manhã', 'Tarde', 'Noite', 'Misto', 'Não trabalho']} />
      </Field>
      <Field label="Tem filhos?">
        <RadioCards name="tem_filhos" value={data.tem_filhos || ''} onChange={onChange} options={['Sim', 'Não']} />
      </Field>
      {data.tem_filhos === 'Sim' && (
        <Field label="Quantos filhos?">
          <input style={S.input} type="number" name="qtd_filhos" value={data.qtd_filhos || ''} onChange={onChange} placeholder="Ex: 2" />
        </Field>
      )}
      <Field label="Está grávida ou amamentando?">
        <RadioCards name="gravida_amamentando" value={data.gravida_amamentando || ''} onChange={onChange}
          options={['Sim', 'Não', 'Não se aplica']} />
      </Field>
    </>
  );
}

function Secao6({ data, onChange }) {
  return (
    <>
      <h2 style={S.sectionTitle}><Ico e="📋" sp />Informações Complementares</h2>
      <Field label="Fez exames de sangue recentemente?">
        <RadioCards name="exames_recentes" value={data.exames_recentes || ''} onChange={onChange} options={['Sim', 'Não']} />
      </Field>
      {data.exames_recentes === 'Sim' && (
        <Field label="Havia alguma alteração nos resultados?">
          <textarea style={S.textarea} name="exames_alteracoes" value={data.exames_alteracoes || ''} onChange={onChange}
            placeholder="Ex: colesterol alto, TSH alterado, vitamina D baixa..." rows={3} />
        </Field>
      )}
      <Field label="Ciclo menstrual regular?" hint="Apenas para mulheres">
        <RadioCards name="ciclo_regular" value={data.ciclo_regular || ''} onChange={onChange}
          options={['Sim', 'Não', 'Não se aplica']} />
      </Field>
      {data.ciclo_regular === 'Não' && (
        <Field label="Tem sintomas de TPM?">
          <RadioCards name="tpm" value={data.tpm || ''} onChange={onChange} options={['Sim', 'Não']} />
        </Field>
      )}
      {data.tpm === 'Sim' && (
        <Field label="Descreva os sintomas de TPM">
          <input style={S.input} name="ciclo_sintomas" value={data.ciclo_sintomas || ''} onChange={onChange} placeholder="Ex: irritabilidade, inchaço, compulsão por doces..." />
        </Field>
      )}
      <Field label="Usa suplementos atualmente?">
        <RadioCards name="usa_suplementos" value={data.usa_suplementos || ''} onChange={onChange} options={['Sim', 'Não']} />
      </Field>
      {data.usa_suplementos === 'Sim' && (
        <Field label="Quais suplementos?">
          <input style={S.input} name="suplementos_desc" value={data.suplementos_desc || ''} onChange={onChange} placeholder="Ex: whey protein, ômega-3, vitamina D..." />
        </Field>
      )}
      <Field label="O que você espera alcançar com o tratamento?" required>
        <textarea style={S.textarea} name="expectativas" value={data.expectativas || ''} onChange={onChange}
          placeholder="Seja à vontade! Conte suas expectativas, metas e sonhos de saúde..." rows={4} />
      </Field>
      <Field label="Algo mais que gostaria de compartilhar com a nutricionista?">
        <textarea style={S.textarea} name="algo_mais" value={data.algo_mais || ''} onChange={onChange}
          placeholder="Qualquer informação adicional que ache relevante..." rows={3} />
      </Field>
    </>
  );
}

// ─── Upload de Exames ─────────────────────────────────────────────────────────

const EXAME_MAX_MB   = 10;
const EXAME_TIPOS_OK = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];

function UploadExames({ exames, onAddExames, onRemoveExame }) {
  const inputRef = useRef(null);
  const [arrastando, setArrastando] = useState(false);
  const [avisos, setAvisos] = useState([]);

  // Valida tipo e tamanho NA SELEÇÃO — o paciente descobre o problema agora,
  // não no fim do formulário quando o upload falharia silenciosamente.
  const adicionarValidando = (files) => {
    const ok = [], rejeitados = [];
    Array.from(files).forEach((f) => {
      if (!EXAME_TIPOS_OK.includes(f.type)) {
        rejeitados.push(`"${f.name}" não é PDF/JPG/PNG`);
      } else if (f.size > EXAME_MAX_MB * 1024 * 1024) {
        rejeitados.push(`"${f.name}" passa de ${EXAME_MAX_MB} MB (${(f.size / 1024 / 1024).toFixed(1)} MB)`);
      } else {
        ok.push(f);
      }
    });
    if (ok.length) onAddExames(ok);
    setAvisos(rejeitados);
  };

  const handleFileChange = (e) => {
    adicionarValidando(e.target.files);
    e.target.value = '';
  };

  return (
    <div style={{ ...S.card, marginTop: '1rem' }}>
      <h2 style={S.sectionTitle}><Ico e="📎" sp />Anexar Exames (opcional)</h2>
      <p style={{ margin: '-0.5rem 0 1rem', fontSize: '0.88rem', color: '#736b64', lineHeight: 1.6 }}>
        Envie seus exames de sangue ou outros exames para que sua nutricionista possa fazer uma avaliação mais completa e personalizada
      </p>

      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setArrastando(true); }}
        onDragLeave={() => setArrastando(false)}
        onDrop={(e) => { e.preventDefault(); setArrastando(false); adicionarValidando(e.dataTransfer.files); }}
        style={{
          border: `2px dashed ${arrastando ? BRAND : BRAND + '80'}`,
          borderRadius: '12px',
          padding: '1.4rem 1rem',
          textAlign: 'center',
          cursor: 'pointer',
          background: arrastando ? `${BRAND}20` : `${BRAND}08`,
          transition: 'border-color 0.2s, background 0.2s',
        }}
      >
        <p style={{ margin: 0, color: '#736b64', fontSize: '0.92rem', fontWeight: 600 }}>
          {emo(arrastando ? '📥 Solte os arquivos aqui' : '📂 Toque para selecionar ou arraste arquivos aqui')}
        </p>
        <p style={{ margin: '0.3rem 0 0', color: '#a39b93', fontSize: '0.78rem' }}>
          PDF, JPG ou PNG · Máx. {EXAME_MAX_MB} MB por arquivo
        </p>
      </div>

      {avisos.length > 0 && (
        <div style={{ marginTop: '0.6rem', background: '#f8ebe9', border: '1px solid #f8ebe9', borderRadius: '8px', padding: '0.6rem 0.8rem', fontSize: '0.8rem', color: '#a8483f' }}>
          {avisos.map((a, i) => <div key={i}><Ico e="⚠" /> {a}</div>)}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        multiple
        accept=".pdf,.jpg,.jpeg,.png"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {exames.length > 0 && (
        <ul style={{ margin: '0.75rem 0 0', padding: 0, listStyle: 'none' }}>
          {exames.map((f, i) => (
            <li
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 0.75rem',
                background: '#f6f0ea',
                borderRadius: '8px',
                marginTop: '0.4rem',
                fontSize: '0.85rem',
              }}
            >
              <span style={{ fontSize: '1rem' }}>
                {emo(f.type === 'application/pdf' ? '📄' : '🖼️')}
              </span>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#2f2a26' }}>
                {f.name}
              </span>
              <span style={{ color: '#736b64', fontSize: '0.75rem', flexShrink: 0 }}>
                {(f.size / 1024 / 1024).toFixed(1)} MB
              </span>
              <button
                type="button"
                onClick={() => onRemoveExame(i)}
                style={{
                  background: 'none', border: 'none', color: '#c46a60',
                  cursor: 'pointer', fontWeight: 600, fontSize: '1.1rem',
                  lineHeight: 1, padding: '0 0.1rem', flexShrink: 0,
                }}
                aria-label="Remover"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Tela de sucesso ──────────────────────────────────────────────────────────

function Sucesso({ nome, relatorioUrl }) {
  return (
    <div style={{ ...S.container, textAlign: 'center', paddingTop: '3rem' }}>
      <div style={{ fontSize: '4rem', marginBottom: '1rem' }}><Ico e="✅" /></div>
      <h1 style={{ fontFamily: 'Nunito, sans-serif', fontSize: '1.8rem', color: '#2f2a26', marginBottom: '0.5rem' }}>
        Ficha enviada com sucesso!
      </h1>
      <p style={{ fontSize: '1rem', color: '#4a4440', lineHeight: 1.7, maxWidth: '380px', margin: '0 auto 1.5rem' }}>
        Sua nutricionista já recebeu suas informações e estará totalmente preparada para a sua consulta!
      </p>

      {relatorioUrl && (
        <>
          <a
            href={relatorioUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block', marginBottom: '0.6rem', padding: '0.85rem 1.6rem',
              background: `linear-gradient(135deg, ${BRAND}, #b8895c)`, color: '#ffffff',
              borderRadius: '12px', fontWeight: 600, fontSize: '0.95rem',
              textDecoration: 'none', boxShadow: '0 4px 14px rgba(212,165,116,0.35)',
            }}
          >
            <Ico e="📄" sp />Baixar o resumo da sua anamnese (PDF)
          </a>
          <p style={{ margin: '0 0 1.5rem', fontSize: '0.82rem', color: '#736b64' }}>
            Também enviamos uma cópia no seu WhatsApp <Ico e="💬" />
          </p>
        </>
      )}

      <div style={{ background: 'linear-gradient(135deg, #fbf8f5, #fbf8f5)', border: `1.5px solid ${BRAND}50`, borderRadius: '16px', padding: '1.5rem', marginBottom: '2rem' }}>
        <p style={{ margin: '0 0 0.5rem', fontSize: '1.4rem' }}><Ico e="🌸" /></p>
        <p style={{ margin: '0 0 0.25rem', fontWeight: 600, color: '#2f2a26' }}>Até breve, {emo(nome?.split(' ')[0] || '')}!</p>
        <p style={{ margin: 0, fontSize: '0.9rem', color: '#736b64' }}>Sua jornada de saúde começa aqui!</p>
      </div>
      <a href="https://www.levnutri.com" style={{ color: BRAND2, fontWeight: 600, fontSize: '0.9rem' }}>
        levnutri.com →
      </a>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

const SECTIONS = [
  { label: 'Identificação', icon: '👤' },
  { label: 'Objetivos',    icon: '🎯' },
  { label: 'Saúde',        icon: '🏥' },
  { label: 'Alimentação',  icon: '🍽️' },
  { label: 'Estilo de Vida', icon: '🏃' },
  { label: 'Complementar', icon: '📋' },
];

const UPLOAD_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8888'}/upload-exames.php`;

export default function AnamnesePublica({ token }) {
  const [step, setStep]         = useState(0);
  const [data, setData]         = useState({});
  const [nomePaciente, setNomePaciente] = useState('');
  const [marcaPub, setMarcaPub] = useState({ logo: '', nome: '' }); // marca da nutricionista dona do link
  const [loading, setLoading]   = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone]         = useState(false);
  const [faseEnvio, setFaseEnvio] = useState('');          // feedback da etapa de envio
  const [relatorioUrl, setRelatorioUrl] = useState(null); // PDF resumo (Fase 5.1)
  const [error, setError]       = useState('');
  const [exames, setExames]     = useState([]);

  const [consentimento, setConsentimento] = useState(false);

  const addExames = useCallback((novos) => {
    setExames(prev => [...prev, ...novos]);
  }, []);

  const removeExame = useCallback((idx) => {
    setExames(prev => prev.filter((_, i) => i !== idx));
  }, []);

  // Pré-preencher nome da URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const nomeUrl = params.get('nome') || '';
    if (nomeUrl) setNomePaciente(decodeURIComponent(nomeUrl));
  }, []);

  // Carregar token do backend
  useEffect(() => {
    if (!token) { setError('Token não informado.'); setLoading(false); return; }

    // Tentar carregar rascunho do localStorage
    const draft = localStorage.getItem(DRAFT_KEY(token));
    if (draft) {
      try { setData(JSON.parse(draft)); } catch {}
    }

    fetch(`${API}/anamnese-publica.php?token=${token}`)
      .then(r => r.json())
      .then(res => {
        if (!res.success) { setError(res.error || 'Link inválido.'); return; }
        if (res.nome_paciente) setNomePaciente(res.nome_paciente);
        if (res.marca) setMarcaPub({
          logo: res.marca.logo_path ? `${API}${res.marca.logo_path}` : '',
          nome: res.marca.business_name || res.marca.nutricionista || '',
        });
        if (res.dados_existentes) {
          setData(prev => ({ ...res.dados_existentes, ...prev }));
        }
        // Pré-preencher nome se veio da URL
        const params = new URLSearchParams(window.location.search);
        const nomeUrl = params.get('nome');
        if (nomeUrl && !res.nome_paciente) setNomePaciente(decodeURIComponent(nomeUrl));
      })
      .catch(() => setError('Erro ao carregar o formulário. Verifique sua conexão.'))
      .finally(() => setLoading(false));
  }, [token]);

  // Auto-save draft
  useEffect(() => {
    if (Object.keys(data).length === 0) return;
    localStorage.setItem(DRAFT_KEY(token), JSON.stringify(data));
  }, [data, token]);

  // Pré-preencher nome no campo
  useEffect(() => {
    if (nomePaciente && !data.nome_completo) {
      setData(prev => ({ ...prev, nome_completo: nomePaciente }));
    }
  }, [nomePaciente]); // eslint-disable-line

  const onChange = useCallback((e) => {
    const { name, value } = e.target;
    setData(prev => ({ ...prev, [name]: value }));
  }, []);

  const onChangeMulti = useCallback((name, values) => {
    setData(prev => ({ ...prev, [name]: values }));
  }, []);

  const handleNext = () => {
    setStep(s => Math.min(s + 1, SECTIONS.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrev = () => {
    setStep(s => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!data.queixa_principal && !data.expectativas) {
      alert('Por favor, preencha pelo menos os campos obrigatórios antes de enviar.');
      return;
    }
    if (!consentimento) {
      alert('Para enviar, marque a caixa confirmando que leu e concorda com a Política de Privacidade.');
      return;
    }
    setSubmitting(true);
    try {
      // 1. Enviar ficha de anamnese (JSON)
      const payload = {
        ...data,
        restricoes_alimentares: (data.restricoes_alimentares_multi || []).join(', '),
        // Aceite LGPD (dado sensível de saúde) — gravado com versão/data/IP no backend
        consentimento: true,
        consent_version: CONSENT_VERSION,
      };
      const res = await fetch(`${API}/anamnese-publica.php?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).then(r => r.json());

      if (!res.success) throw new Error(res.error || 'Erro ao enviar');

      // 2. Enviar exames se houver (multipart/form-data) — falha silenciosa
      if (exames.length > 0) {
        setFaseEnvio(`Enviando ${exames.length} exame(s)...`);
        const formData = new FormData();
        formData.append('token', token);
        exames.forEach(f => formData.append('exames[]', f));
        await fetch(UPLOAD_URL, { method: 'POST', body: formData }).catch(() => {});
      }

      localStorage.removeItem(DRAFT_KEY(token));
      if (res.relatorio_url) setRelatorioUrl(res.relatorio_url);
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      alert(e.message || 'Erro ao enviar. Tente novamente.');
    } finally {
      setSubmitting(false);
      setFaseEnvio('');
    }
  };

  // ── Render states ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ ...S.wrap, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#736b64' }}>
          <div style={{ width: 48, height: 48, border: '4px solid #f6f0ea', borderTopColor: BRAND, borderRadius: '50%', animation: 'spin 0.9s linear infinite', margin: '0 auto 1rem' }} />
          <p>Carregando...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...S.wrap, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '1rem', padding: '2rem' }}>
        <span style={{ fontSize: '3rem' }}><Ico e="⚠" /></span>
        <h2 style={{ color: '#2f2a26', textAlign: 'center', fontFamily: 'Nunito, sans-serif' }}>Link inválido</h2>
        <p style={{ color: '#736b64', textAlign: 'center', maxWidth: '320px' }}>{error}</p>
        <a href="https://www.levnutri.com" style={{ color: BRAND2, fontWeight: 600 }}>levnutri.com →</a>
      </div>
    );
  }

  if (done) {
    return (
      <div style={S.wrap}>
        <header style={S.header}>
          <img src={marcaPub.logo || LOGO_URL} alt={marcaPub.nome || 'LevNutri'} style={{ height: '36px', objectFit: 'contain' }} onError={e => { e.target.style.display = 'none'; }} />
        </header>
        <Sucesso nome={data.nome_completo || nomePaciente} relatorioUrl={relatorioUrl} />
      </div>
    );
  }

  const progress = ((step + 1) / SECTIONS.length) * 100;

  const sectionProps = { data, onChange, onChangeMulti };

  return (
    <div style={S.wrap}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
        input[type=range]::-webkit-slider-thumb { background: ${BRAND}; }
        * { box-sizing: border-box; }
      `}</style>

      {/* Header */}
      <header style={S.header}>
        <img src={marcaPub.logo || LOGO_URL} alt={marcaPub.nome || 'LevNutri'} style={{ height: '36px', objectFit: 'contain' }} onError={e => { e.target.style.display = 'none'; }} />
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontSize: '0.8rem', fontWeight: 600, color: '#2f2a26' }}>Ficha de Anamnese</p>
          {nomePaciente && <p style={{ margin: 0, fontSize: '0.72rem', color: '#736b64' }}>{nomePaciente}</p>}
        </div>
        <span style={{ fontSize: '0.78rem', color: '#736b64', fontWeight: 600 }}>
          {step + 1}/{SECTIONS.length}
        </span>
      </header>

      <div style={S.container}>
        {/* Barra de progresso */}
        <div style={S.progress}>
          <div style={{ height: '100%', width: `${progress}%`, background: `linear-gradient(90deg, ${BRAND}, #e8c9a8)`, borderRadius: '999px', transition: 'width 0.4s ease' }} />
        </div>
        <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: '#736b64', textAlign: 'right' }}>
          Seção {step + 1} de {SECTIONS.length} — {emo(SECTIONS[step].label)}
        </p>

        {/* Pills das seções */}
        <div style={{ display: 'flex', gap: '0.35rem', overflowX: 'auto', padding: '0.75rem 0 0.25rem', scrollbarWidth: 'none' }}>
          {SECTIONS.map((s, i) => (
            <button key={i} onClick={() => { setStep(i); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              style={{
                flexShrink: 0, padding: '0.3rem 0.65rem',
                borderRadius: '999px', border: `1.5px solid ${i === step ? BRAND : '#d9d0c6'}`,
                background: i === step ? `${BRAND}20` : i < step ? '#edf4ee' : '#ffffff',
                color: i === step ? BRAND2 : i < step ? '#46714f' : '#736b64',
                fontSize: '0.75rem', fontWeight: i === step ? 700 : 500,
                cursor: 'pointer', fontFamily: 'inherit',
              }}>
              {emo(i < step ? '✓' : s.icon)} {emo(s.label)}
            </button>
          ))}
        </div>

        {/* Conteúdo da seção */}
        <div style={S.card}>
          {step === 0 && <Secao1 {...sectionProps} />}
          {step === 1 && <Secao2 {...sectionProps} />}
          {step === 2 && <Secao3 {...sectionProps} />}
          {step === 3 && <Secao4 {...sectionProps} />}
          {step === 4 && <Secao5 {...sectionProps} />}
          {step === 5 && <Secao6 {...sectionProps} />}
        </div>

        {/* Upload de exames — aparece apenas na última etapa */}
        {step === SECTIONS.length - 1 && (
          <UploadExames
            exames={exames}
            onAddExames={addExames}
            onRemoveExame={removeExame}
          />
        )}

        {/* Navegação */}
        {step < SECTIONS.length - 1 ? (
          <>
            <button style={S.btnNext} onClick={handleNext}>
              Próximo →
            </button>
            {step > 0 && (
              <button style={S.btnPrev} onClick={handlePrev}>
                ← Anterior
              </button>
            )}
          </>
        ) : (
          <>
            <label
              htmlFor="consentimento-lgpd"
              style={{
                display: 'flex', gap: '0.65rem', alignItems: 'flex-start', cursor: 'pointer',
                background: '#ffffff', border: `1.5px solid ${consentimento ? '#46714f' : '#d9d0c6'}`,
                borderRadius: '12px', padding: '0.9rem 1rem', marginTop: '1rem',
                fontSize: '0.85rem', lineHeight: 1.55, color: '#2f2a26',
              }}
            >
              <input
                id="consentimento-lgpd"
                type="checkbox"
                checked={consentimento}
                onChange={e => setConsentimento(e.target.checked)}
                style={{ width: 20, height: 20, marginTop: 2, flexShrink: 0, accentColor: '#46714f' }}
              />
              <span>
                Li e concordo com a{' '}
                <a href="/privacidade/" target="_blank" rel="noopener noreferrer" style={{ color: BRAND2, fontWeight: 600 }}>
                  Política de Privacidade
                </a>{' '}
                e autorizo o uso dos meus dados de saúde, exames e fotos para o meu atendimento
                nutricional, inclusive com apoio de inteligência artificial revisado pela nutricionista.
                Se estou preenchendo por um menor de idade, declaro ser o responsável legal.
              </span>
            </label>
            <button
              style={{ ...S.btnNext, background: submitting || !consentimento ? '#d9d0c6' : 'linear-gradient(135deg, #46714f, #6f9a77)' }}
              onClick={handleSubmit}
              disabled={submitting || !consentimento}
              aria-disabled={submitting || !consentimento}
            >
              {emo(submitting ? (faseEnvio || 'Enviando sua ficha...') : '✅ Enviar Ficha')}
            </button>
            <button style={S.btnPrev} onClick={handlePrev}>← Anterior</button>
          </>
        )}

        <p style={{ textAlign: 'center', fontSize: '0.72rem', color: '#d9d0c6', marginTop: '1rem' }}>
          <Ico e="🔒" sp />Suas informações são confidenciais e protegidas. Rascunho salvo automaticamente neste aparelho.{' '}
          <a href="/privacidade/" target="_blank" rel="noopener noreferrer" style={{ color: '#a39b93' }}>Política de Privacidade</a>
        </p>
      </div>
    </div>
  );
}
